import { useCallback, useEffect, useState } from 'react';

const POLL_MS = 5000;

export function useMaintenanceNotice() {
  const [notice, setNotice] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  const refresh = useCallback(async () => {
    try {
      const response = await fetch('/api/maintenance', { cache: 'no-store' });
      if (!response.ok) return;
      const data = await response.json();
      setNotice({
        phase: data.phase || 'idle',
        message: data.message || '',
        endsAt: data.endsAt || null,
        serverNow: data.serverNow || null,
        id: data.id || null,
        receivedAt: Date.now(),
      });
    } catch {
      // Keep the last notice. A failed poll during an update should leave the banner up.
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  return { notice, now, refresh };
}

export function remainingMs(notice, now) {
  if (!notice?.endsAt || !notice?.serverNow) return null;
  const ends = Date.parse(notice.endsAt);
  const server = Date.parse(notice.serverNow);
  if (Number.isNaN(ends) || Number.isNaN(server)) return null;
  return ends - (server + (now - notice.receivedAt));
}

function formatRemaining(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const secondsLabel = String(seconds).padStart(2, '0');
  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, '0')}m ${secondsLabel}s`;
  }
  return `${minutes}m ${secondsLabel}s`;
}

const barStyle = {
  flexShrink: 0,
  width: '100%',
  background: '#f5c518',
  color: '#1a1400',
  padding: '0.65rem 1.25rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '0.75rem',
  flexWrap: 'wrap',
  fontWeight: 600,
  fontSize: '0.95rem',
  lineHeight: 1.4,
  textAlign: 'center',
  zIndex: 50,
};

export function MaintenanceBanner() {
  const { notice, now } = useMaintenanceNotice();
  if (!notice || notice.phase === 'idle') return null;

  const remaining = remainingMs(notice, now);
  const updating = notice.phase === 'updating' || remaining == null || remaining <= 0;

  return (
    <div role="status" style={barStyle}>
      {notice.message ? <span>{notice.message}</span> : null}
      {updating ? <span>Updating now.</span> : <span>Updates in {formatRemaining(remaining)}</span>}
    </div>
  );
}

export function MaintenanceUpdatingNotice() {
  const { notice } = useMaintenanceNotice();
  if (notice?.phase !== 'updating') return null;

  return (
    <div
      role="status"
      style={{
        ...barStyle,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100000,
        padding: '0.75rem 1rem',
        fontSize: '1rem',
      }}
    >
      The server is updating. Try again in a few minutes.
    </div>
  );
}
